<script setup lang="ts">
import {ref, onMounted, inject, watch, nextTick} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import BrandedLogo from './components/BrandedLogo.vue';
import {formatPhone} from './utils';
import {PortalAuthKey} from './plugins/auth';
import AuthLayout from "./layouts/AuthLayout.vue";

type CodeType = "sms" | "email";

// Core Vue & plugin setup
const router = useRouter();
const route = useRoute();

const auth = inject(PortalAuthKey);
if (!auth) throw new Error("Auth plugin not found");

// State
const loading = ref(false);
const success = ref(false);
const errorAlert = ref(false);
const errorMsg = ref<string>();
const optInput = ref('');
const codeSendType = ref<CodeType>();
const step = ref(1);
const timer = ref(30);
const resendEnabled = ref(false);
const errorMessageSnackbar = ref(false);
const errorMessage = ref('');

const otpRef = ref(); // Used for autofocus

const token = route.query.access_token as string;
const callbackUrl = route.query.callbackUrl as string;
const clientId = route.query.clientId as string;

const verifyCode = async () => {
  const [ok, msg] = await auth.verifyOtpCode(
      optInput.value,
      codeSendType.value!,
      token,
      clientId,
      callbackUrl
  );

  if (ok) {
    success.value = true;
    console.log('sent to landing from twofa.vue: ', msg);
  } else {
    errorAlert.value = true;
    errorMsg.value = msg ?? "OTP verification failed";
  }
};

const sendCode = async (method: CodeType) => {
  resendEnabled.value = false;
  loading.value = true;
  timer.value = 30;
  codeSendType.value = method;
  setInterval(decrementTimer, 1000);

  const sent = await auth.sendOtpCode(method, token);
  loading.value = false;
  if (sent) {
    step.value = 2;
    await nextTick();
    otpRef.value?.focus?.(); // Ensure it’s available
  } else {
    errorMessage.value = "Failed to send OTP code.";
    errorMessageSnackbar.value = true;
  }
};

const resendCode = () => {
  timer.value = 30;
  if (codeSendType.value) {
    sendCode(codeSendType.value);
  }
};

const decrementTimer = () => {
  if (timer.value > 0) {
    timer.value--;
  } else {
    resendEnabled.value = true;
  }
};

watch(loading, async (newValue) => {
  if (newValue === false && step.value === 2) {
    await nextTick();
    otpRef.value?.focus?.();
  }
})

const cancelOtpFlow = async () => {
  await auth.logout();
  await router.push({name: 'app.login'});
};

onMounted(() => {
});
</script>

<template>
  <AuthLayout>
    <v-sheet v-if="!loading && !success" width="450" class="mx-auto pa-12 text-center" rounded="lg" elevation="8">
      <BrandedLogo/>
      <br>
      <v-alert v-if="errorAlert" color="error">
        <p>{{ errorMsg }}</p>
      </v-alert>
      <br>

      <!-- Step 1: Choose method -->
      <div v-if="step === 1">
        <v-alert color="info" text="Please select how you want to receive your OTP."/>
        <br>
        <v-list class="text-left">
          <v-list-item class="my-2" rounded lines="two" prepend-icon="mdi-email" title="Email"
                       :subtitle="auth.authState.user?.email"
                       @click="sendCode('email')"
                       :disabled="!auth.authState.user?.email"/>
          <v-list-item class="my-2" rounded lines="two" prepend-icon="mdi-message" title="SMS"
                       :subtitle="formatPhone(auth.authState.user?.phone)"
                       @click="sendCode('sms')"
                       :disabled="!auth.authState.user?.phone"/>
        </v-list>
        <v-divider/>
        <v-btn class="my-2" block color="primary" @click="cancelOtpFlow">Cancel</v-btn>
      </div>

      <!-- Step 2: Enter code -->
      <div v-else-if="step === 2">
        <v-alert color="info" text="Please enter the code sent to your mobile device."/>
        <br>
        <v-otp-input ref="otpRef" v-model="optInput" autofocus/>
        <v-btn color="primary" block class="my-2" size="large" @click="verifyCode">
          <span class="mx-2">Verify</span>
        </v-btn>
        <span v-if="resendEnabled" class="my-2 text-caption">
          Didn't receive a code?
          <span class="text-caption text-primary cursor-pointer" @click="resendCode">Resend OTP</span>
        </span>
        <span v-else>Resend available in {{ timer }}s</span>
      </div>
    </v-sheet>

    <v-sheet v-else-if="loading" width="450" class="mx-auto pa-12 text-center" rounded="lg" elevation="8">
      <v-progress-circular :size="100" :width="8" color="primary" indeterminate/>
    </v-sheet>

    <v-sheet v-else-if="success && !loading" width="450" class="mx-auto pa-12 text-center" rounded="lg" elevation="8">
      <v-icon class="mb-5" color="success" icon="mdi-check-circle" size="112"/>
      <h2 class="text-h5 mb-6">Success!</h2>
      <p class="mb-4 text-medium-emphasis text-body-2">You will be redirected momentarily.</p>
    </v-sheet>

    <v-snackbar v-model="errorMessageSnackbar">
      {{ errorMessage }}
      <template v-slot:actions>
        <v-btn color="pink" variant="text" @click="errorMessageSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </AuthLayout>
</template>
